import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useAuth } from "../../contexts/AuthContext";
import { TelegramContext } from "../../contexts/TelegramContext";
import { tg, puedeTelegram, myIdTg, esTelegramApagado, errorTg, codigoTg, olvidarTodasLasClaves, LIMITE_CHATS } from "../../lib/telegram";
import TgBurbuja from "./TgBurbuja";
import "./telegram.css";

const UI_KEY = "tg.ui.v1";
const VACIO = { items: [], total: 0, counts: { all: 0, groups: 0, personal: 0, bots: 0 }, send: false, listo: false, error: "" };

function leerUi() {
  try {
    const v = JSON.parse(localStorage.getItem(UI_KEY) || "{}");
    return { modo: v.modo === "ancla" ? "ancla" : "burbuja", grande: !!v.grande, ancho: [400, 560].includes(v.ancho) ? v.ancho : 400 };
  } catch (e) { return { modo: "burbuja", grande: false, ancho: 400 }; }
}
const esperar = (ms) => new Promise((r) => setTimeout(r, ms));

/**
 * Estado global del Chat con Telegram: estado de la cuenta, lista de chats (los que ESTE usuario puede ver), no-leídos,
 * aviso de mensaje nuevo y la burbuja/ancla sobre cualquier módulo. Un solo sondeo largo (/updates) por ventana.
 * Telegram apagado en el servidor (503) = todo en reposo: sin burbuja y sin más peticiones.
 */
export default function TelegramProvider({ children }) {
  const { user } = useAuth();
  const uid = myIdTg(user);
  const habilitado = puedeTelegram(user);
  const [carga, setCarga] = useState("cargando");   // cargando | ok | apagado | error
  const [status, setStatus] = useState(null);
  const [ownerSel, setOwnerSel] = useState("");
  const [lista, setLista] = useState(VACIO);
  const [sync, setSync] = useState("ok");
  const [alerta, setAlerta] = useState(null);
  const [ui, setUi] = useState(() => ({ ...leerUi(), abierto: false, chatActivo: "" }));
  const viva = useRef(true);
  const reqStatus = useRef(0);
  const reqChats = useRef(0);
  const prevNoLeidos = useRef(null);
  const alertaTimer = useRef(null);

  useEffect(() => { viva.current = true; return () => { viva.current = false; clearTimeout(alertaTimer.current); }; }, []);

  // ---- dueño efectivo: el dueño de cuenta usa la suya (sin owner_id); el empleado, la del gerente que elija ----
  const compartidos = useMemo(() => (status?.role === "employee" ? status.shared_with_me || [] : []), [status]);
  const owner = useMemo(() => {
    if (status?.role !== "employee") return "";
    const lista2 = status.shared_with_me || [];
    if (ownerSel && lista2.some((o) => o.owner_id === ownerSel)) return ownerSel;
    return (lista2.find((o) => o.owner_id && o.owner_connected) || lista2[0] || {}).owner_id || "";
  }, [status, ownerSel]);
  const puedeLeer = !!status && ["conectado", "modo_lectura"].includes(status.state);

  // ---- otra persona en este navegador: nada se arrastra (ni claves de «Solo yo») ----
  useEffect(() => {
    olvidarTodasLasClaves();
    setStatus(null); setOwnerSel(""); setLista(VACIO); setAlerta(null); prevNoLeidos.current = null;
    setUi((u) => ({ ...u, abierto: false, chatActivo: "" }));
    setCarga(habilitado ? "cargando" : "ok");
  }, [uid, habilitado]);

  const refreshStatus = useCallback(async () => {
    if (!habilitado) return;
    const mio = ++reqStatus.current;
    try {
      const r = await tg.get("/status");
      if (!viva.current || mio !== reqStatus.current) return;
      setStatus(r.data); setCarga("ok");
    } catch (e) {
      if (!viva.current || mio !== reqStatus.current) return;
      setStatus(null); setCarga(esTelegramApagado(e) ? "apagado" : "error");
    }
  }, [habilitado]);
  useEffect(() => { refreshStatus(); }, [refreshStatus, uid]);

  const refreshChats = useCallback(async () => {
    if (!habilitado || !puedeLeer) return;
    const mio = ++reqChats.current;
    try {
      const r = await tg.get("/chats", { params: { limit: LIMITE_CHATS, ...(owner ? { owner_id: owner } : {}) } });
      if (!viva.current || mio !== reqChats.current) return;
      const d = r.data || {};
      const items = Array.isArray(d.items) ? d.items : [];
      // aviso en la burbuja: un chat visible con más no-leídos que antes (la primera carga no avisa)
      const antes = prevNoLeidos.current;
      if (antes) {
        const nuevo = items.find((c) => !c.locked && !(c.last && c.last.out) && (c.unread || 0) > (antes[c.chat_id] ?? 0));
        if (nuevo) {
          setAlerta({ chat_id: nuevo.chat_id, title: nuevo.title, text: nuevo.last?.text || "Mensaje nuevo", ts: Date.now() });
          clearTimeout(alertaTimer.current);
          alertaTimer.current = setTimeout(() => { if (viva.current) setAlerta(null); }, 9000);
        }
      }
      prevNoLeidos.current = Object.fromEntries(items.map((c) => [c.chat_id, c.locked ? 0 : c.unread || 0]));
      setLista({ items, total: d.total ?? items.length, counts: d.folder_counts || VACIO.counts, send: !!d.send_enabled, listo: true, error: "" });
    } catch (e) {
      if (!viva.current || mio !== reqChats.current) return;
      if (esTelegramApagado(e) || [404, 409].includes(e?.response?.status)) { setLista({ ...VACIO, listo: true, error: errorTg(e) }); refreshStatus(); return; }
      setLista((l) => ({ ...l, listo: true, error: errorTg(e) }));
    }
  }, [habilitado, puedeLeer, owner, refreshStatus]);
  useEffect(() => { prevNoLeidos.current = null; setLista(VACIO); }, [owner, puedeLeer]);
  useEffect(() => { refreshChats(); }, [refreshChats]);

  // ---- sondeo largo: solo avisa QUÉ cambió; el contenido se pide aparte. Nunca cada 1,5 s. ----
  useEffect(() => {
    if (!habilitado || !puedeLeer) return undefined;
    let vivo = true;
    (async () => {
      let cursor = null;
      while (vivo) {
        const t0 = Date.now();
        const conCursor = !!cursor;
        try {
          const r = await tg.get("/updates", { params: { ...(owner ? { owner_id: owner } : {}), ...(cursor ? { cursor, timeout: 20 } : { timeout: 0 }) }, timeout: 35000 });
          if (!vivo) return;
          cursor = r.data?.cursor ?? cursor;
          setSync("ok");
          if (r.data?.events?.length) refreshChats();
          else if (conCursor && Date.now() - t0 < 500) await esperar(1000);   // defensa: un servidor que no espera no debe inundarse
        } catch (e) {
          if (!vivo) return;
          setSync("bad");
          const st = e?.response?.status;
          if (esTelegramApagado(e) || st === 404 || st === 403 || codigoTg(e) === "not_connected") { refreshStatus(); return; }
          await esperar(5000);
        }
      }
    })();
    return () => { vivo = false; };
  }, [habilitado, puedeLeer, owner, refreshChats, refreshStatus]);

  // ---- burbuja / ancla ----
  const guardarUi = (n) => { try { localStorage.setItem(UI_KEY, JSON.stringify({ modo: n.modo, grande: n.grande, ancho: n.ancho })); } catch (e) { /* sin almacenamiento */ } };
  const cambiar = useCallback((parte) => setUi((u) => { const n = { ...u, ...parte }; guardarUi(n); return n; }), []);
  const abrirBurbuja = useCallback(() => cambiar({ abierto: true }), [cambiar]);
  const cerrarBurbuja = useCallback(() => cambiar({ abierto: false }), [cambiar]);
  const setModo = useCallback((modo) => cambiar({ modo, abierto: true }), [cambiar]);
  const setGrande = useCallback((grande) => cambiar({ grande }), [cambiar]);
  const setAncho = useCallback((ancho) => cambiar({ ancho }), [cambiar]);
  const setChatActivo = useCallback((chatActivo) => cambiar({ chatActivo }), [cambiar]);
  const abrirChat = useCallback((chatActivo) => { setAlerta(null); cambiar({ chatActivo, abierto: true }); }, [cambiar]);
  const cerrarAlerta = useCallback(() => setAlerta(null), []);

  const unreadTotal = useMemo(() => lista.items.reduce((n, c) => n + (c.locked ? 0 : c.unread || 0), 0), [lista.items]);

  const value = useMemo(() => ({
    carga, status, owner, setOwner: setOwnerSel, compartidos, puedeLeer,
    chats: lista.items, total: lista.total, folderCounts: lista.counts, sendEnabled: lista.send, chatsListos: lista.listo, chatsError: lista.error,
    unreadTotal, sync, alerta, cerrarAlerta, refreshStatus, refreshChats,
    ui, abrirBurbuja, cerrarBurbuja, setModo, setGrande, setAncho, setChatActivo, abrirChat,
  }), [carga, status, owner, compartidos, puedeLeer, lista, unreadTotal, sync, alerta, cerrarAlerta, refreshStatus, refreshChats, ui, abrirBurbuja, cerrarBurbuja, setModo, setGrande, setAncho, setChatActivo, abrirChat]);

  return (
    <TelegramContext.Provider value={value}>
      {children}
      {habilitado && carga === "ok" && puedeLeer && <TgBurbuja />}
    </TelegramContext.Provider>
  );
}
